/// <amd-module name="Dashboards/DashboardView/src/redux/sagas/RefreshSaga" />
import { PageVisibility } from "Dashboards/Common/PageVisibility";
import { DashboardThrottlingInfo, IDashboardThrottlingService } from "Dashboards/RateLimiter/DashboardThrottlingService";
import { effects, eventChannel, SagaIterator } from "redux-saga";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { DashboardActions, DashboardActionTypes } from "../actions/Actions";
import { TelemetryActions } from "../actions/TelemetryActions";
import * as Selectors from "../selectors/Selectors";

export namespace DashboardRefreshSagas {
    /** Supplementary auto-refresh saga to be included in a module and run by 'redux-saga' */
    export function* refreshSaga(): SagaIterator {
        yield effects.takeLatest(DashboardActionTypes.StartAutoRefreshTimer, startTimerSaga);
        yield effects.takeLatest(DashboardActionTypes.StopAutoRefreshTimer, stopTimerSaga);
    }

    function* startTimerSaga(): SagaIterator {
        const refreshTimeMinutes = (yield effects.select(Selectors.getRefreshInterval)) as any;

        if (refreshTimeMinutes > 0) {
            // Enable refresh time display
            yield effects.put(DashboardActions.toggleRefreshTimeDisplay(true));

            const timer = effects.call(startAutoRefreshTimer);
            const cancelTimer = effects.take(DashboardActionTypes.StopAutoRefreshTimer);

            // The timer saga has an infinite loop, so its task will never end the race.
            // When the cancel action is dispatched, the race ends, and the timer task is canceled.
            yield effects.race({ timer, cancelTimer });
        }
    }

    function* stopTimerSaga(): SagaIterator {
        yield effects.put(DashboardActions.toggleRefreshTimeDisplay(false));
    }

    function* startAutoRefreshTimer(): SagaIterator {
        // Set timer to initial value
        yield effects.put(DashboardActions.updateRefreshTimer());

        const startTime = (yield effects.select(Selectors.getRenderTimestamp)) as any;
        const channel = (yield effects.call(createTimerChannel, startTime)) as any;

        const refreshIntervalInMinutes = (yield effects.select(Selectors.getRefreshInterval)) as any;

        // Interval, in MS
        const refreshInterval = refreshIntervalInMinutes * 60 * 1000;

        // In addition to the normal refresh cycle, we have less frequent hard-refresh cycle.
        // This periodically runs a page-level refresh to guarantee the client has current bits for client-server compat, and guards against browser level IFrame leakage issues
        const hardRefreshInterval = 12 * refreshInterval;
        const hardRefreshDeadline = startTime + hardRefreshInterval;

        try {
            while (true) {
                // This yields when setInterval below triggers
                const elapsedTime = (yield effects.take(channel)) as any;

                const isThrottlingWarningActive = (yield effects.select(Selectors.isThrottlingWarningActive)) as any;
                const isThrottlingWarningAllowed = (yield effects.select(Selectors.isThrottlingAllowed)) as any;

                // Depending on how much time passed, we either hard refresh the page, refresh content of dashboard or update header
                if (elapsedTime >= refreshInterval) {
                    const pageVisible = (yield effects.call(PageVisibility.isPageVisible)) as any;
                    if (pageVisible) {
                        yield effects.call(publishAutoRefreshCycleTelemetry);

                        if (!(isThrottlingWarningAllowed && isThrottlingWarningActive)) {
                            // If the page has been up for longer than the hard refresh deadline, request a hard refresh
                            const isHardRefresh = Date.now() >= hardRefreshDeadline;
                            yield effects.put(DashboardActions.refreshDashboard(true, isHardRefresh));
                        }
                    }
                } else {
                    // Conditionally hide refresh time display if throttling is going on
                    yield effects.put(DashboardActions.toggleRefreshTimeDisplay(!(isThrottlingWarningAllowed && isThrottlingWarningActive)));
                    yield effects.put(DashboardActions.updateRefreshTimer());
                }
            }
        } finally {
            // Runs when timer task is cancelled by the race
            if (yield effects.cancelled()) {
                // Close the channel so that clearInterval is called
                channel.close();
            }
        }
    }

    function* publishAutoRefreshCycleTelemetry(): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const throttlingService: IDashboardThrottlingService = (yield effects.call(
            [pageContext, pageContext.getService],
            "IDashboardThrottlingService"
        )) as any;
        const isMonitoringEnabled = (yield effects.call(
            isFeatureFlagEnabled,
            pageContext,
            "WebAccess.Dashboards.MonitorResourceUtilizationHeaders"
        )) as any;

        const isThrottlingWarningActive = (yield effects.select(Selectors.isThrottlingWarningActive)) as any;
        // Peeking into throttling service to get current throttling state - since we don't really change page state when this
        // This will be replaced with proper state selector once FF is removed and we start keeping isThrottled in state
        const throttlingState: DashboardThrottlingInfo = (yield effects.call([throttlingService, throttlingService.getThrottlingState])) as any;

        const autoRefreshTelemetryProps: { [key: string]: string | number } = {
            isUtilizationDetectionActive: isMonitoringEnabled,
            isThrottlingBlockingRefresh: isThrottlingWarningActive,
            isThrottlingThresholdExceeded: throttlingState.isThrottled as any // Note this logic is different from original implementation, and sets this to true on throttling even if throttling is not allowed
        };

        yield effects.put(TelemetryActions.publishAutoRefreshCycleTelemetry(autoRefreshTelemetryProps));
    }

    function createTimerChannel(startTime: number = Date.now()) {
        return eventChannel(emitter => {
            // Consumers of the channel yield on the emitter callback
            const iv = setInterval(() => {
                emitter(Date.now() - startTime);
            }, 5000);

            // This is "unsubscribe" callback of channels which gets called when task terminates
            return () => {
                clearInterval(iv);
            };
        });
    }
}
